// Restricted (c) Siemens Healthineers AG, 2025, All rights reserved.

import { CommonModule } from '@angular/common';
import {
  ChangeDetectorRef,
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  EventEmitter,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslateModule } from '@ngx-translate/core';

import { SystemDetailsModel } from 'src/app/api/remote-api/.portal.generated/models';
import { DnaSyngoviaDataConnections } from '../../interfaces/dna-syngovia-data-connections';
import {
  DnaSyngoviaGroupNode,
  DnaSyngoviaGroupNodeEType,
} from '../../interfaces/dna-syngovia-group-node';
import { ConnectionsRectService } from '../../services/chart-map/connections-rect.service';
import { ModalityChartMapService } from '../../services/chart-map/modality-chart-map.service';
import { DnaSyngoviaGroupNodeCardComponent } from '../dna-syngovia-group-node-card/dna-syngovia-group-node-card.component';
import { DnaSyngoviaChartMapLegendComponent } from '../dna-syngovia-chart-map-legend/dna-syngovia-chart-map-legend.component';

@Component({
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatTooltipModule,
    DnaSyngoviaGroupNodeCardComponent,
    DnaSyngoviaChartMapLegendComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  selector: 'app-dna-syngovia-chart-map',
  templateUrl: './dna-syngovia-chart-map.component.html',
  styleUrls: ['./dna-syngovia-chart-map.component.scss'],
})
export class DnaSyngoviaChartMapComponent implements OnInit {
  @Input({ required: true }) systemDetails!: SystemDetailsModel;
  @Input({ required: true }) dataConnections!: DnaSyngoviaDataConnections;
  @Output() selectedGroupNodeChange = new EventEmitter<DnaSyngoviaGroupNode>();

  public selectedGroupNode: DnaSyngoviaGroupNode | undefined;

  protected chartWidth = 0;
  protected chartHeight = 0;
  protected serverHeight = 0;
  protected viewBox = '';

  protected legendX = 0;
  protected legendY = 0;
  protected legendWidth = 0;
  protected legendHeight = 24;

  protected readonly LEGEND_SPACE = 40;

  protected readonly connectionsRectByType =
    this.connectionsRectService.newConnectionsRectByType();

  protected connectionsRectByTypeKeys = Object.keys(this.connectionsRectByType);

  public constructor(
    private readonly connectionsRectService: ConnectionsRectService,
    private readonly modalityChartMapService: ModalityChartMapService,
    private readonly changeDetectorRef: ChangeDetectorRef,
  ) {}

  public ngOnInit(): void {
    this.drawChartMap();
    this.changeDetectorRef.detectChanges();
  }

  private drawChartMap(): void {
    if (this.dataConnections) {
      this.initConnectionsRect();
      this.initChartDimensions();
      this.calculateConnectionsRectValues();
    }
  }

  private calculateConnectionsRectValues() {
    let type = DnaSyngoviaGroupNodeEType.SERVER;
    let rects = this.connectionsRectByType[type];
    this.connectionsRectService
      .serviceByType(type)
      ?.calculateRectValues(rects, 0, 0, this.serverHeight);

    this.legendX = rects[0].rect.x;
    this.legendY = rects[0].rect.height + 16;
    this.legendWidth = rects[0].rect.width;

    type = DnaSyngoviaGroupNodeEType.MODALITY;
    rects = this.connectionsRectByType[type];
    this.connectionsRectService.serviceByType(type)?.calculateRectValues(rects);

    type = DnaSyngoviaGroupNodeEType.DIGITAL_PRODUCT;
    rects = this.connectionsRectByType[type];
    let values = this.connectionsRectService
      .serviceByType(type)
      ?.calculateRectValues(rects);

    type = DnaSyngoviaGroupNodeEType.CLIENT;
    rects = this.connectionsRectByType[type];
    values = this.connectionsRectService
      .serviceByType(type)
      ?.calculateRectValues(rects, values ? values.row : 0, 0);

    type = DnaSyngoviaGroupNodeEType.DICOM_PROXY;
    rects = this.connectionsRectByType[type];
    this.connectionsRectService
      .serviceByType(type)
      ?.calculateRectValues(rects, values ? values.row : 0, 0);
  }

  private initChartDimensions() {
    this.chartWidth = this.modalityChartMapService.chartWidth();
    this.chartHeight = this.modalityChartMapService.chartHeight(
      this.connectionsRectByType,
    );

    this.chartHeight += this.LEGEND_SPACE;

    this.serverHeight = this.modalityChartMapService.serverHeight(
      this.connectionsRectByType,
    );

    this.viewBox = `0 0 ${this.chartWidth} ${this.chartHeight}`;
  }

  private initConnectionsRect() {
    this.connectionsRectByTypeKeys.forEach((type) =>
      this.connectionsRectService.filterConnections(
        this.connectionsRectByType,
        this.dataConnections,
        type,
      ),
    );
  }

  public onSelectedGroupNodeChange(groupNode: DnaSyngoviaGroupNode): void {
    this.selectedGroupNode = groupNode;
    this.selectedGroupNodeChange.emit(groupNode);
  }

  protected isCardSelected(groupNode: DnaSyngoviaGroupNode): boolean {
    return this.selectedGroupNode === groupNode;
  }
}
